import React, {useEffect, useState} from "react";
import styles from '../../styles/dashboard.module.css';
import connStyles from './connections.module.css';
import Header from "../../components/header/header";
import axiosInstance from "../../AxiosInstance";
import PageTitle from "../../components/page_title/page_title";
import Modal from "../../components/modal/modal";
import {TextInput, SelectInput} from "../../components/input/inputs";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {faEdit, faPlay, faSpinner} from "@fortawesome/free-solid-svg-icons";
import {useTranslation} from "react-i18next";
import PaginationControls, {getStoredPageSize} from "../../components/pagination/PaginationControls";

export default function Connections() {
    const {t} = useTranslation();
    const project_id = parseInt(localStorage.getItem('project_id'));
    const [mqttConnections, setMqttConnections] = useState([]);
    const [apiConnections, setApiConnections] = useState([]);
    const [loading, setLoading] = useState(true);

    // Edit modal
    const [showEditModal, setShowEditModal] = useState(false);
    const [editForm, setEditForm] = useState({});
    const [editingType, setEditingType] = useState(null); // 'mqtt' | 'api'
    const [editingId, setEditingId] = useState(null);

    // Test
    const [testingId, setTestingId] = useState(null);
    const [testingType, setTestingType] = useState(null);
    const [testResult, setTestResult] = useState(null);
    const [testLoading, setTestLoading] = useState(false);

    // Filter
    const [filter, setFilter] = useState('all'); // 'all' | 'mqtt' | 'api'
    const [page, setPage] = useState(1);
    const [pageSize, setPageSize] = useState(getStoredPageSize);

    useEffect(() => {
        fetchConnections();
    }, []);

    useEffect(() => {
        setPage(1);
    }, [filter]);

    const fetchConnections = () => {
        setLoading(true);
        Promise.all([
            axiosInstance.get(`mqtt_connection/?project_id=${project_id}`),
            axiosInstance.get(`api_connection/?project_id=${project_id}`)
        ]).then(([mqttRes, apiRes]) => {
            setMqttConnections(Array.isArray(mqttRes.data) ? mqttRes.data : (mqttRes.data.results || []));
            setApiConnections(Array.isArray(apiRes.data) ? apiRes.data : (apiRes.data.results || []));
            setLoading(false);
        }).catch(() => setLoading(false));
    };

    const openEditModal = (type, id) => {
        setEditingType(type);
        setEditingId(id);
        const endpoint = type === 'mqtt' ? `mqtt_connection/${id}/` : `api_connection/${id}/`;
        axiosInstance.get(endpoint).then((res) => {
            setEditForm(res.data);
            setShowEditModal(true);
        });
    };

    const saveEdit = () => {
        const endpoint = editingType === 'mqtt' ? `mqtt_connection/${editingId}/` : `api_connection/${editingId}/`;
        const payload = {...editForm};
        // Remove read-only fields
        delete payload.id;
        delete payload.created_at;
        delete payload.updated_at;
        delete payload.created_by;
        delete payload.created_by_username;
        delete payload.has_password;
        delete payload.has_auth;
        delete payload.has_token;
        delete payload.has_api_key;
        delete payload.auth_type_display;

        axiosInstance.put(endpoint, payload).then(() => {
            setShowEditModal(false);
            fetchConnections();
        });
    };

    const testConnection = (type, id) => {
        setTestingId(id);
        setTestingType(type);
        setTestLoading(true);
        setTestResult(null);

        const endpoint = type === 'mqtt'
            ? `mqtt_connection/${id}/test/`
            : `api_connection/${id}/test/`;

        const body = type === 'mqtt'
            ? {topic: 'test/connection', message: 'ping'}
            : {method: 'GET', endpoint: '', body: null};

        axiosInstance.post(endpoint, body).then((res) => {
            setTestResult({success: true, message: res.data.message || t('connections.connectionSuccessful')});
            setTestLoading(false);
        }).catch((err) => {
            setTestResult({success: false, message: err.response?.data?.error || t('connections.connectionFailed')});
            setTestLoading(false);
        });
    };

    const formatDate = (dateStr) => {
        if (!dateStr) return '-';
        const d = new Date(dateStr);
        return d.toLocaleDateString() + ' ' + d.toLocaleTimeString([], {hour: '2-digit', minute: '2-digit'});
    };

    const authTypeOptions = [
        {id: 'none', name: t('connections.authTypes.none')},
        {id: 'basic', name: t('connections.authTypes.basic')},
        {id: 'bearer', name: t('connections.authTypes.bearer')},
        {id: 'api_key', name: t('connections.authTypes.apiKey')},
    ];

    const getFilteredConnections = () => {
        const mqtt = mqttConnections.map(c => ({...c, _type: 'mqtt'}));
        const api = apiConnections.map(c => ({...c, _type: 'api'}));
        if (filter === 'mqtt') return mqtt;
        if (filter === 'api') return api;
        return [...mqtt, ...api];
    };

    const handlePageSizeChange = (newSize) => { setPageSize(newSize); setPage(1); };

    const allConnections = getFilteredConnections();
    const totalPages = Math.ceil(allConnections.length / pageSize);
    const pagedConnections = allConnections.slice((page - 1) * pageSize, page * pageSize);

    return (
        <div className={styles.dashboard_container}>
            <Header sidebarIndex={8}/>

            <Modal show={showEditModal} setter={setShowEditModal} name={t('connections.editTitle', {type: editingType === 'mqtt' ? 'MQTT' : 'API'})} buttonName={t('common.save')} onClick={saveEdit}>
                <TextInput name={t('common.name')} internalName={'name'} form={editForm} setForm={setEditForm}/>

                {editingType === 'mqtt' && (
                    <>
                        <TextInput name={t('connections.broker')} internalName={'broker'} form={editForm} setForm={setEditForm}/>
                        <TextInput name={t('connections.portCol')} internalName={'port'} form={editForm} setForm={setEditForm}/>
                        <div className={connStyles.checkboxRow}>
                            <input
                                type="checkbox"
                                id="use_tls"
                                checked={editForm.use_tls || false}
                                onChange={(e) => setEditForm({...editForm, use_tls: e.target.checked})}
                            />
                            <label htmlFor="use_tls"><b>{t('connections.useTls')}</b></label>
                        </div>
                        {editForm.use_tls && (
                            <div className={connStyles.checkboxRow}>
                                <input
                                    type="checkbox"
                                    id="mqtt_verify_ssl"
                                    checked={editForm.verify_ssl ?? true}
                                    onChange={(e) => setEditForm({...editForm, verify_ssl: e.target.checked})}
                                />
                                <label htmlFor="mqtt_verify_ssl"><b>{t('connections.verifySsl')}</b></label>
                            </div>
                        )}
                        <TextInput name={t('connections.username')} internalName={'username'} form={editForm} setForm={setEditForm}/>
                        <TextInput name={t('connections.password')} internalName={'password'} form={editForm} setForm={setEditForm} password/>
                        <TextInput name={t('connections.clientId')} internalName={'client_id'} form={editForm} setForm={setEditForm}/>
                    </>
                )}

                {editingType === 'api' && (
                    <>
                        <TextInput name={t('connections.baseUrl')} internalName={'base_url'} form={editForm} setForm={setEditForm}/>
                        <SelectInput name={t('connections.authType')} internalName={'auth_type'} form={editForm} setForm={setEditForm} options={authTypeOptions}/>

                        {editForm.auth_type === 'basic' && (
                            <>
                                <TextInput name={t('connections.username')} internalName={'auth_username'} form={editForm} setForm={setEditForm}/>
                                <TextInput name={t('connections.password')} internalName={'auth_password'} form={editForm} setForm={setEditForm} password/>
                            </>
                        )}
                        {editForm.auth_type === 'bearer' && (
                            <TextInput name={t('connections.token')} internalName={'auth_token'} form={editForm} setForm={setEditForm} password/>
                        )}
                        {editForm.auth_type === 'api_key' && (
                            <>
                                <TextInput name={t('connections.apiKeyHeader')} internalName={'api_key_header'} form={editForm} setForm={setEditForm}/>
                                <TextInput name={t('connections.apiKeyValue')} internalName={'api_key_value'} form={editForm} setForm={setEditForm} password/>
                            </>
                        )}

                        <TextInput name={t('connections.timeout')} internalName={'timeout_seconds'} form={editForm} setForm={setEditForm}/>
                        <div className={connStyles.checkboxRow}>
                            <input
                                type="checkbox"
                                id="verify_ssl"
                                checked={editForm.verify_ssl ?? true}
                                onChange={(e) => setEditForm({...editForm, verify_ssl: e.target.checked})}
                            />
                            <label htmlFor="verify_ssl"><b>{t('connections.verifySsl')}</b></label>
                        </div>
                    </>
                )}
            </Modal>

            <div className={styles.dashboardInnerContainer}>
                <PageTitle title={t('connections.title')}/>

                <div className={connStyles.sectionToggle}>
                    <div className={`${connStyles.toggleBtn} ${filter === 'all' ? connStyles.toggleBtnActive : ''}`} onClick={() => setFilter('all')}>
                        {t('common.all')} ({mqttConnections.length + apiConnections.length})
                    </div>
                    <div className={`${connStyles.toggleBtn} ${filter === 'mqtt' ? connStyles.toggleBtnActive : ''}`} onClick={() => setFilter('mqtt')}>
                        MQTT ({mqttConnections.length})
                    </div>
                    <div className={`${connStyles.toggleBtn} ${filter === 'api' ? connStyles.toggleBtnActive : ''}`} onClick={() => setFilter('api')}>
                        API ({apiConnections.length})
                    </div>
                </div>

                {loading ? (
                    <div className={connStyles.emptyState}>{t('connections.loadingConnections')}</div>
                ) : allConnections.length === 0 ? (
                    <div className={connStyles.emptyState}>{t('connections.noConnectionsFound')}</div>
                ) : (<>
                    {/* Desktop table */}
                    <div className={connStyles.tableWrapper}>
                        <table className={connStyles.table}>
                            <thead>
                                <tr>
                                    <th>{t('common.name')}</th>
                                    <th>{t('connections.typeCol')}</th>
                                    <th>{t('connections.hostUrl')}</th>
                                    <th>{t('connections.portCol')}</th>
                                    <th>{t('connections.authCol')}</th>
                                    <th>{t('connections.updatedCol')}</th>
                                    <th>{t('common.actions')}</th>
                                </tr>
                            </thead>
                            <tbody>
                                {pagedConnections.map((conn) => {
                                    const isMqtt = conn._type === 'mqtt';
                                    const connKey = `${conn._type}-${conn.id}`;
                                    const isTestingThis = testingId === conn.id && testingType === conn._type;
                                    return (
                                        <tr key={connKey}>
                                            <td style={{fontWeight: 500}}>{conn.name}</td>
                                            <td><span className={isMqtt ? connStyles.badgeMqtt : connStyles.badgeApi}>{isMqtt ? 'MQTT' : 'API'}</span></td>
                                            <td>{isMqtt ? conn.broker : conn.base_url}</td>
                                            <td>{isMqtt ? conn.port : '-'}</td>
                                            <td>{isMqtt ? (conn.has_auth ? t('connections.authUsernamePassword') : t('common.none')) : (conn.auth_type_display || conn.auth_type || t('common.none'))}</td>
                                            <td>{formatDate(conn.updated_at)}</td>
                                            <td>
                                                <button className={connStyles.actionBtn} onClick={() => openEditModal(conn._type, conn.id)} title={t('common.edit')}><FontAwesomeIcon icon={faEdit}/></button>
                                                <button className={connStyles.actionBtn} onClick={() => testConnection(conn._type, conn.id)} title={t('connections.test')}>
                                                    {isTestingThis && testLoading ? <FontAwesomeIcon icon={faSpinner} spin/> : <FontAwesomeIcon icon={faPlay}/>}
                                                </button>
                                                {isTestingThis && testResult && (
                                                    <span className={testResult.success ? connStyles.testSuccess : connStyles.testError}>{testResult.message}</span>
                                                )}
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>

                    {/* Mobile cards */}
                    <div className={connStyles.mobileCards}>
                        {pagedConnections.map((conn) => {
                            const isMqtt = conn._type === 'mqtt';
                            const connKey = `${conn._type}-${conn.id}`;
                            const isTestingThis = testingId === conn.id && testingType === conn._type;
                            return (
                                <div key={connKey} className={connStyles.connCard}>
                                    <div className={connStyles.connCardHeader}>
                                        <div className={connStyles.connCardName}>{conn.name}</div>
                                        <span className={isMqtt ? connStyles.badgeMqtt : connStyles.badgeApi}>
                                            {isMqtt ? 'MQTT' : 'API'}
                                        </span>
                                    </div>
                                    <div className={connStyles.connCardMeta}>
                                        <span><b>{t('connections.hostUrl')}:</b> {isMqtt ? conn.broker : conn.base_url}</span>
                                        {isMqtt && conn.port && <span><b>{t('connections.portCol')}:</b> {conn.port}</span>}
                                        <span><b>{t('connections.authCol')}:</b> {isMqtt ? (conn.has_auth ? t('connections.authUsernamePassword') : t('common.none')) : (conn.auth_type_display || conn.auth_type || t('common.none'))}</span>
                                        <span><b>{t('connections.updatedCol')}:</b> {formatDate(conn.updated_at)}</span>
                                    </div>
                                    <div className={connStyles.connCardActions}>
                                        <button className={connStyles.actionBtn} onClick={() => openEditModal(conn._type, conn.id)}>
                                            <FontAwesomeIcon icon={faEdit}/> {t('common.edit')}
                                        </button>
                                        <button className={connStyles.actionBtn} onClick={() => testConnection(conn._type, conn.id)}>
                                            {isTestingThis && testLoading ? <FontAwesomeIcon icon={faSpinner} spin/> : <FontAwesomeIcon icon={faPlay}/>} {t('connections.test')}
                                        </button>
                                        {isTestingThis && testResult && (
                                            <span className={testResult.success ? connStyles.testSuccess : connStyles.testError}>{testResult.message}</span>
                                        )}
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                    <PaginationControls
                        page={page}
                        totalPages={totalPages}
                        pageSize={pageSize}
                        onPageChange={setPage}
                        onPageSizeChange={handlePageSizeChange}
                        totalCount={allConnections.length}
                    />
                </>)}
            </div>
        </div>
    );
}
